import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiPlus, FiSearch, FiFilter, FiRefreshCw, FiEdit2, FiTrash2, FiEye, FiArrowLeft, FiX, FiCalendar } from "react-icons/fi";
import CircularProgress from "@mui/material/CircularProgress";
import { remindersApi } from "./remindersApi";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import { formatDate } from "../Common Components/CommonFunction";
import "./Reminders.css";

const STATUS_CONFIG = {
    ACTIVE: { label: "Active", class: "success" },
    EXPIRING_SOON: { label: "Expiring Soon", class: "warning" },
    EXPIRED: { label: "Expired", class: "danger" },
    RENEWED: { label: "Renewed", class: "info" },
    CANCELLED: { label: "Cancelled", class: "muted" },
};

const PRIORITY_CONFIG = {
    CRITICAL: { label: "Critical", class: "danger" },
    HIGH: { label: "High", class: "warning" },
    MEDIUM: { label: "Medium", class: "info" },
    LOW: { label: "Low", class: "success" },
};

const formatCurrency = (val) => {
    if (!val && val !== 0) return "-";
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(val);
};

const getDaysLabel = (days) => {
    if (days === null || days === undefined) return "-";
    if (days < 0) return `${Math.abs(days)}d overdue`;
    if (days === 0) return "Today";
    return `${days}d left`;
};

const RemindersList = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    const [filters, setFilters] = useState({
        status: searchParams.get("status") || "",
        category: searchParams.get("category") || "",
        priority: searchParams.get("priority") || "",
        search: "",
        sort: "expiry_date",
        order: "asc",
    });

    const fetchItems = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.category) params.category = filters.category;
            if (filters.priority) params.priority = filters.priority;
            if (filters.search) params.search = filters.search;
            params.sort = filters.sort;
            params.order = filters.order;

            const res = await remindersApi.getItems(params);
            if (res.status === "success") setItems(res.data || []);
        } catch (err) {
            showErrorToast(err.message);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await remindersApi.getCategories();
            if (res.status === "success") setCategories(res.data || []);
        } catch (err) { /* silent */ }
    }, []);

    useEffect(() => { fetchItems(); }, [fetchItems]);
    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Delete reminder "${title}"?`)) return;
        try {
            const res = await remindersApi.deleteItem(id);
            if (res.status === "success") {
                showSuccessToast("Reminder deleted");
                fetchItems();
            }
        } catch (err) {
            showErrorToast(err.message);
        }
    };

    const clearFilters = () => {
        setFilters({ status: "", category: "", priority: "", search: "", sort: "expiry_date", order: "asc" });
    };

    const activeFilterCount = [filters.status, filters.category, filters.priority].filter(Boolean).length;

    return (
        <div className="rem-page">
            <div className="rem-header">
                <div className="rem-header__left">
                    <button className="rem-btn rem-btn--ghost" onClick={() => navigate("/reminders")}>
                        <FiArrowLeft size={16} />
                    </button>
                    <div>
                        <h1 className="rem-title">All Reminders</h1>
                        <p className="rem-subtitle">{items.length} items found</p>
                    </div>
                </div>
                <div className="rem-header-actions">
                    <div className="rem-search">
                        <FiSearch size={14} className="rem-search__icon" />
                        <input
                            type="text"
                            placeholder="Search by title, code, provider..."
                            value={filters.search}
                            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                            className="rem-search__input"
                        />
                    </div>
                    <button className={`rem-btn rem-btn--outline ${activeFilterCount > 0 ? "rem-btn--active" : ""}`} onClick={() => setShowFilters(!showFilters)}>
                        <FiFilter size={14} /> Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                    </button>
                    <button className="rem-btn rem-btn--outline" onClick={fetchItems}>
                        <FiRefreshCw size={14} />
                    </button>
                    <button className="rem-btn rem-btn--primary" onClick={() => navigate("/reminders/new")}>
                        <FiPlus size={14} /> New
                    </button>
                </div>
            </div>

            {/* Filters Bar */}
            {showFilters && (
                <div className="rem-filters">
                    <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="rem-select">
                        <option value="">All Status</option>
                        <option value="ACTIVE">Active</option>
                        <option value="EXPIRING_SOON">Expiring Soon</option>
                        <option value="EXPIRED">Expired</option>
                        <option value="RENEWED">Renewed</option>
                        <option value="CANCELLED">Cancelled</option>
                    </select>
                    <select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} className="rem-select">
                        <option value="">All Categories</option>
                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} className="rem-select">
                        <option value="">All Priorities</option>
                        <option value="CRITICAL">Critical</option>
                        <option value="HIGH">High</option>
                        <option value="MEDIUM">Medium</option>
                        <option value="LOW">Low</option>
                    </select>
                    <select value={`${filters.sort}_${filters.order}`} onChange={(e) => {
                        const [s, o] = e.target.value.split("_");
                        setFilters((f) => ({ ...f, sort: s, order: o }));
                    }} className="rem-select">
                        <option value="expiry_date_asc">Expiry: Soonest</option>
                        <option value="expiry_date_desc">Expiry: Latest</option>
                        <option value="amount_desc">Amount: High to Low</option>
                        <option value="amount_asc">Amount: Low to High</option>
                        <option value="created_time_desc">Newest First</option>
                    </select>
                    {activeFilterCount > 0 && (
                        <button className="rem-btn rem-btn--ghost rem-btn--sm" onClick={clearFilters}>
                            <FiX size={14} /> Clear
                        </button>
                    )}
                </div>
            )}

            {/* Table */}
            {loading ? (
                <div className="rem-loading"><CircularProgress size={32} sx={{ color: "var(--accent)" }} /></div>
            ) : items.length === 0 ? (
                <div className="rem-empty">
                    <FiCalendar size={48} className="rem-empty__icon" />
                    <h3>No reminders found</h3>
                    <p>Create your first reminder to start tracking expirations</p>
                    <button className="rem-btn rem-btn--primary" onClick={() => navigate("/reminders/new")}>
                        <FiPlus size={14} /> Create Reminder
                    </button>
                </div>
            ) : (
                <div className="rem-table-wrap">
                    <table className="rem-table">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Title</th>
                                <th>Category</th>
                                <th>Provider</th>
                                <th>Expiry Date</th>
                                <th>Days Left</th>
                                <th>Amount</th>
                                <th>Priority</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => {
                                const sc = STATUS_CONFIG[item.status] || { label: item.status, class: "muted" };
                                const pc = PRIORITY_CONFIG[item.priority] || { label: item.priority, class: "muted" };
                                return (
                                    <tr key={item.id} className="rem-table__row">
                                        <td className="rem-table__code">{item.reminder_code}</td>
                                        <td className="rem-table__title" onClick={() => navigate(`/reminders/${item.id}`)}>{item.title}</td>
                                        <td>
                                            <span className="rem-cat-badge" style={{ borderColor: item.category_color || "#7c5cfc" }}>
                                                <span className="rem-cat-badge__dot" style={{ background: item.category_color || "#7c5cfc" }} />
                                                {item.category_name}
                                            </span>
                                        </td>
                                        <td>{item.provider_name || "-"}</td>
                                        <td>{formatDate(item.expiry_date)}</td>
                                        <td>
                                            <span className={`rem-days ${item.days_until_expiry < 0 ? "rem-days--overdue" : item.days_until_expiry <= 7 ? "rem-days--urgent" : item.days_until_expiry <= 30 ? "rem-days--soon" : ""}`}>
                                                {getDaysLabel(item.days_until_expiry)}
                                            </span>
                                        </td>
                                        <td className="rem-table__amount">{formatCurrency(item.amount)}</td>
                                        <td><span className={`rem-badge rem-badge--${pc.class}`}>{pc.label}</span></td>
                                        <td><span className={`rem-badge rem-badge--${sc.class}`}>{sc.label}</span></td>
                                        <td className="rem-table__actions">
                                            <button className="rem-icon-btn" title="View" onClick={() => navigate(`/reminders/${item.id}`)}>
                                                <FiEye size={14} />
                                            </button>
                                            <button className="rem-icon-btn" title="Edit" onClick={() => navigate(`/reminders/edit/${item.id}`)}>
                                                <FiEdit2 size={14} />
                                            </button>
                                            <button className="rem-icon-btn rem-icon-btn--danger" title="Delete" onClick={() => handleDelete(item.id, item.title)}>
                                                <FiTrash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default RemindersList;
